<template>
  <div v-if="open" class="modal fade show d-block brand-modal" tabindex="-1" aria-modal="true" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow-lg rounded-4">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ isEdit ? $t("brand_page.edit_chip") : $t("brand_page.new_chip") }}</span>
            <h5 class="modal-title fw-bold mt-3 mb-1">
              {{ isEdit ? $t("brand_page.update_title") : $t("brand_page.create_title") }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t("brand_page.modal_description") }}
            </p>
          </div>
          <button type="button" class="btn-close shadow-none" :aria-label="$t('common.close')" @click="$emit('close')"></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("brand_page.code") }}</label>
                <span class="field-caption">{{ $t("brand_page.code_caption") }}</span>
              </div>
              <div class="reference-input-wrap">
                <input
                  :value="form.code"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': errors.code }"
                  :placeholder="$t('brand_page.code_placeholder')"
                  @input="$emit('update-field', 'code', ($event.target as HTMLInputElement).value)"
                />
                <button
                  v-if="!isEdit"
                  type="button"
                  class="btn btn-light generate-code-btn"
                  :disabled="loading"
                  @click="$emit('generate-code')"
                >
                  <i class="ri-refresh-line me-1"></i>
                  {{ $t("brand_page.generate") }}
                </button>
              </div>
              <div v-if="errors.code" class="invalid-feedback d-block">{{ errors.code[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("brand_page.name") }}</label>
                <span class="field-caption">{{ $t("brand_page.name_caption") }}</span>
              </div>
              <input
                :value="form.name"
                type="text"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.name }"
                :placeholder="$t('brand_page.name_placeholder')"
                @input="$emit('update-field', 'name', ($event.target as HTMLInputElement).value)"
              />
              <div v-if="errors.name" class="invalid-feedback d-block">{{ errors.name[0] }}</div>
            </div>

            <div class="col-md-7">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("brand_page.description") }}</label>
                <span class="field-caption">{{ $t("brand_page.description_caption") }}</span>
              </div>
              <textarea
                :value="form.description"
                rows="5"
                class="form-control modal-input modal-textarea"
                :placeholder="$t('brand_page.description_placeholder')"
                @input="$emit('update-field', 'description', ($event.target as HTMLTextAreaElement).value)"
              ></textarea>
            </div>

            <div class="col-md-5">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("brand_page.image") }}</label>
                <span class="field-caption">{{ $t("brand_page.image_caption") }}</span>
              </div>
              <label class="upload-shell">
                <input type="file" class="upload-input" accept="image/*" @change="$emit('file-change', $event)" />
                <img v-if="previewUrl" :src="previewUrl" class="upload-preview" :alt="$t('brand_page.image_preview_alt')" />
                <div v-else class="upload-placeholder">
                  <img :src="fallbackImage" :alt="$t('brand_page.image_placeholder_alt')" width="36" class="mb-2 opacity-50" />
                  <p class="mb-1 fw-semibold text-title">{{ $t("brand_page.upload_image") }}</p>
                  <span class="small text-muted">{{ $t("brand_page.image_hint") }}</span>
                </div>
              </label>
              <button
                v-if="previewUrl && !isEdit"
                type="button"
                class="btn btn-light btn-sm mt-2"
                @click="$emit('remove-image')"
              >
                {{ $t("brand_page.remove_image") }}
              </button>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("brand_page.status") }}</label>
                <span class="field-caption">{{ $t("brand_page.status_caption") }}</span>
              </div>
              <select
                :value="String(form.status ?? '1')"
                class="form-select modal-input"
                @change="$emit('update-field', 'status', ($event.target as HTMLSelectElement).value)"
              >
                <option value="1">{{ $t("brand_page.status_active") }}</option>
                <option value="0">{{ $t("brand_page.status_inactive") }}</option>
              </select>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
            <button type="button" class="btn btn-light px-4" @click="$emit('close')">
              {{ $t("common.cancel") }}
            </button>
            <button type="button" class="btn style-one px-4 h-55" :disabled="loading" @click="$emit('save')">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? $t("brand_page.saving") : isEdit ? $t("brand_page.update_action") : $t("brand_page.create_action") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { Brand } from "@/services/brand";
import defaultBrandImg from "@/assets/img/icons/brand.svg";

export default defineComponent({
  name: "CreateBrandModal",
  emits: ["close", "save", "generate-code", "update-field", "file-change", "remove-image"],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object as PropType<Brand>,
      required: true,
    },
    errors: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    previewUrl: {
      type: String,
      default: "",
    },
  },
  setup() {
    return {
      fallbackImage: defaultBrandImg,
    };
  },
});
</script>

<style scoped>
.brand-modal {
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  color: #6b7280;
  font-size: 12px;
}

.reference-input-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: stretch;
}

.modal-input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #fff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
}

.modal-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.modal-textarea {
  min-height: 140px;
  resize: vertical;
  padding-top: 16px;
}

.generate-code-btn {
  min-height: 54px;
  padding: 0 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #f8faff;
  font-weight: 600;
  white-space: nowrap;
}

.upload-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 214px;
  border: 1px dashed #cdd7f0;
  border-radius: 20px;
  background: #f8faff;
  cursor: pointer;
  overflow: hidden;
}

.upload-input {
  display: none;
}

.upload-placeholder {
  text-align: center;
  padding: 24px;
}

.upload-preview {
  width: 100%;
  height: 214px;
  object-fit: cover;
}
</style>
